import axios from "axios";
import {getOrgFromBrowserStorage, logout, switchSelectedOrganization} from "@/services/userService";
import {handleExpectedApiError, handleUnexpectedApiError} from "@/services/serviceHelper";
import router from "@/router";
import store from '../store'
import {SET_MOBILEAPPVERSION} from "@/store/mutation-types";
import {guessCurrentTimezone} from "@/js/shared/helper";

let instance = axios.create({
})

const onFulfilled = (success) => {
    // let temp = success;
    
    // router.push('/login')
    if (success && success.headers && success.headers['x-latest-mobile-app-version']) {
        store.commit(SET_MOBILEAPPVERSION, success.headers['x-latest-mobile-app-version']);
    }

    // saml methods sometimes return 302. leaving it like this till I know if it auto redirect or not
    if (success.status == 302)
        return success;
    
    // The PDF APIs do not return an "isSuccess" datapoint
    if (!('isSuccess' in success.data)) return success.data
    
    if (!success.data.isSuccess) {
        return handleExpectedApiError(success.data)
    }
    return success.data
}

//add request interceptor to make sure current OrgId matches the one in the token
instance.interceptors.request.use(async (config) => {

    config.headers['ClientTimeZone'] = guessCurrentTimezone();

    let lowercasedUrl = config.url.toLowerCase();

    if (lowercasedUrl.endsWith('api/account/switchselectedorganization')) return config;

    if (lowercasedUrl.endsWith('api/account/requestmfacode')) return config;

    if (lowercasedUrl.endsWith('api/account/verifymfacode')) return config;

    //javascript for params, stackoverflow: https://stackoverflow.com/questions/901115/how-can-i-get-query-string-values-in-javascript
    //get orgId from query string
    const params = new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop) => searchParams.get(prop),
    });

    // Get the value of "some_key" in eg "https://example.com/?some_key=some_value"
    let orgId = params.orgId;

    if (!orgId) return config;

    //get selectedOrganizationId from user object
    let org = getOrgFromBrowserStorage();
    if (!org) return config;

    if (!org.id) return config;

    //If the URL's orgId doesn't match the stored selected org, wait for a switch to
    //complete before this request goes out. switchSelectedOrganization deduplicates
    //concurrent callers via an in-flight promise, so 10 racing requests collapse to
    //one POST. After the switch settles, write the URL-requested orgId onto this
    //request's header so it goes out with the correct value (axios defaults are
    //merged at request-creation time and aren't picked up after the fact).
    if (orgId != org.id) {
        const switchResponse = await switchSelectedOrganization(orgId);

        // Backend says the user doesn't have access to this org. Route to the
        // not-found page and cancel this request — it would otherwise go out with
        // the wrong org context.
        if (switchResponse && switchResponse.isSuccess && !switchResponse.selectedOrganization) {
            router.push({ name: 'Organization Not Found' });
            return Promise.reject(new axios.Cancel('Organization not accessible'));
        }

        config.headers['Selected-Organization-Id'] = orgId;
    }

    return config;
});

instance.interceptors.response.use(onFulfilled, error => {
    // Cancellations from the request interceptor (e.g. routing to Organization
    // Not Found) shouldn't surface as user-facing errors.
    if (axios.isCancel(error)) {
        return Promise.reject(error);
    }

    const expectedError =
        error.response &&
        error.response.status >= 400 &&
        error.response.status < 500;

    if (error.response && error.response.status === 401) {
        logout()
    }

    if (!expectedError) {
        console.log("Logging the error", error);
        //
        return handleUnexpectedApiError(error.response)
    } else {
        return handleExpectedApiError(error.response)
    }

    return Promise.reject(error)
});

// instance.defaults.headers.common['Access-Control-Allow-Origin'] = '*';

instance.defaults.headers.common['Authorization'] = 'Bearer ' + localStorage.getItem('jwtToken')
instance.defaults.headers.common['Selected-Organization-Id'] = sessionStorage.getItem('selectedOrganizationId')
instance.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
instance.defaults.headers.common['Referrer-Policy'] = 'no-referrer';

const setAuthTokenHeader = (token) => {
    instance.defaults.headers.common['Authorization'] = 'Bearer ' + token;
}

const removeAuthTokenHeader = (token) => {
    delete instance.defaults.headers.common["Authorization"];
}

const setSelectedOrgId = (orgId) => {
    instance.defaults.headers.common['Selected-Organization-Id'] = orgId;
}

// Separate instance for unauthenticated endpoints (forgot-password, reset-password, etc.)
let publicInstance = axios.create({})
publicInstance.interceptors.response.use(onFulfilled, error => {
    const expectedError =
        error.response &&
        error.response.status >= 400 &&
        error.response.status < 500;

    if (!expectedError) {
        console.log("Logging the error", error);
        return handleUnexpectedApiError(error.response)
    } else {
        return handleExpectedApiError(error.response)
    }
})

const postPublic = (url, data) => {
    return publicInstance.post(url, data);
}

export default {
    get: instance.get,
    post: instance.post,
    put: instance.put,
    delete: instance.delete,
    postPublic,
    removeAuthTokenHeader,
    setAuthTokenHeader,
    setSelectedOrgId
}